import { BLOCKED_TYPE_TEXT, BlockedCourseChipGroup } from '@exam-center/blocked-user-management';
import { BlockedRecordBlockedScope, type BlockedRecordBlockedType } from '@exam-center/graphql';
import { Grid, Text } from '@shared/components';
import { formatDate } from '@shared/utils';
import React from 'react';

import {
  BlockedRecordAccordion,
  type BlockedRecordAccordionProps,
} from '../blocked-record-accordion';

interface Content {
  label: string;
  value: string | React.ReactNode;
}

function renderExamCourses(
  blockedScope: BlockedRecordBlockedScope,
  examCourseInfos: { code: string; name: string }[] | null,
) {
  if (blockedScope === BlockedRecordBlockedScope.AllScope) {
    return 'ระงับสิทธิ์ทุกหลักสูตร';
  }

  return <BlockedCourseChipGroup examCourseInfos={examCourseInfos ?? []} />;
}

export interface BlockedRecordStaffAccordionProps
  extends Pick<BlockedRecordAccordionProps, 'endAdornment'> {
  blockedType: BlockedRecordBlockedType;
  blockedScope: BlockedRecordBlockedScope;
  blockedAt: Date;
  blockedUntil: Date | null;
  examCenterCode: string | null;
  examCenterName: string;
  examCourseInfos: { code: string; name: string }[] | null;
  blockedReason: string | null;
  metadata: {
    position: string | null;
    department: string | null;
    enforcementEndDate: Date | null;
  } | null;
}

export default function BlockedRecordStaffAccordion(
  props: Readonly<BlockedRecordStaffAccordionProps>,
) {
  const {
    blockedType,
    blockedUntil,
    blockedAt,
    blockedScope,
    examCenterCode,
    examCenterName,
    examCourseInfos,
    blockedReason,
    metadata,
    endAdornment,
  } = props;

  const content: Array<Content> = [
    {
      label: 'ประเภทผู้ถูกระงับสิทธิ์',
      value: 'ผู้ปฏิบัติหน้าที่',
    },
    {
      label: 'ตำแหน่ง',
      value: metadata?.position ?? '-',
    },
    {
      label: 'ฝ่ายงาน/ส่วนงาน',
      value: metadata?.department ?? '-',
    },
    {
      label: 'วันที่เริ่มปฏิบัติหน้าที่',
      value: formatDate(blockedAt),
    },
    {
      label: 'วันที่สิ้นสุดปฏิบัติหน้าที่',
      value: metadata?.enforcementEndDate ? formatDate(metadata.enforcementEndDate) : '-',
    },
    {
      label: 'วันที่สิ้นสุดระงับสิทธิ์',
      value: blockedUntil ? formatDate(blockedUntil) : '-',
    },
    {
      label: 'หลักสูตรที่ระงับสิทธิ์สอบ',
      value: renderExamCourses(blockedScope, examCourseInfos),
    },
    {
      label: 'ศูนย์ทดสอบ',
      value: examCenterCode ? `${examCenterCode}: ${examCenterName ?? ''}` : '-',
    },
    {
      label: 'หมายเหตุ',
      value: blockedReason ?? '-',
    },
  ];

  return (
    <BlockedRecordAccordion
      label='ประเภทหน้าที่'
      title={BLOCKED_TYPE_TEXT[blockedType]}
      blockedUntil={blockedUntil}
      examCenterCode={examCenterCode}
      endAdornment={endAdornment}
    >
      <Grid
        sx={(theme) => ({
          flexDirection: 'column',
          gap: theme.spacing(6),
          p: theme.spacing(4),
          gridTemplateColumns: '240px 1fr',
        })}
      >
        {content.map(({ label, value }) => (
          <React.Fragment key={label}>
            <Text variant='body1Semibold'>{label}</Text>
            {typeof value === 'string' ? <Text>{value}</Text> : value}
          </React.Fragment>
        ))}
      </Grid>
    </BlockedRecordAccordion>
  );
}
